/**
 * Back to where you came from (ADR 0076). When a navigation lands on another page (a link, a
 * drawer, a toast, the palette, a notification), the new history entry remembers the view it
 * came from in its React Router state, so the top bar can offer "Back to <origin>", browser Back
 * and Alt+Left agree with it, and a reload keeps it. Moves inside one page (tabs, filters, a
 * drawer, Settings sections) keep the origin the page was entered from.
 */
import { navForPath } from './nav.ts';

/** The key in `location.state` that holds the origin. */
export const ORIGIN_STATE = 'orgusFrom';

export interface Origin {
  /** Path, query and hash of the view to return to. */
  href: string;
  /** Its name as the top bar showed it ("Sessions", "Launch settings"). */
  label: string;
  /** Its history index (React Router's `history.state.idx`), so Back can step to that very entry. */
  idx: number | null;
}

/** A view as the tracker saw it last: where it is and what it was entered from. */
export interface View {
  href: string;
  pathname: string;
  label: string;
  idx: number | null;
  origin: Origin | null;
}

export type NavigationKind = 'PUSH' | 'REPLACE' | 'POP';

export function readOrigin(state: unknown): Origin | null {
  if (!state || typeof state !== 'object') return null;
  const o = (state as Record<string, unknown>)[ORIGIN_STATE] as Partial<Origin> | undefined;
  if (!o || typeof o.href !== 'string' || typeof o.label !== 'string') return null;
  return { href: o.href, label: o.label, idx: typeof o.idx === 'number' ? o.idx : null };
}

export function withOrigin(state: unknown, origin: Origin): Record<string, unknown> {
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  return { ...base, [ORIGIN_STATE]: origin };
}

/** The page a path belongs to: its nav item's route, else the path itself (Getting started, Not found). */
export function pageOf(pathname: string): string {
  return navForPath(pathname)?.to ?? pathname;
}

/**
 * The origin a new history entry should carry, or 'unchanged' to leave its state alone.
 * Back and forward (POP) never rewrite an entry; an entry that already names its origin keeps it;
 * a direct load has none. Inside one page, and on a replace (a redirect, a filter), the entry
 * inherits the origin of the view it follows; a push to another page comes from that view.
 */
export function originFor(prev: View | null, next: { pathname: string; state: unknown }, kind: NavigationKind): Origin | 'unchanged' {
  if (kind === 'POP' || readOrigin(next.state) || !prev) return 'unchanged';
  if (kind === 'REPLACE' || pageOf(prev.pathname) === pageOf(next.pathname)) return prev.origin ?? 'unchanged';
  return { href: prev.href, label: prev.label, idx: prev.idx };
}

/**
 * How many steps back the origin is, for `navigate(delta)`: stepping back lands on the very
 * entry, with its query, state and scroll, the way browser Back does. Null when it cannot be
 * stepped to (unknown index), and the caller opens the origin's address instead.
 */
export function backDelta(origin: Origin, currentIdx: number | null): number | null {
  if (origin.idx === null || currentIdx === null || origin.idx >= currentIdx) return null;
  return origin.idx - currentIdx;
}

/** The current history entry's index, as React Router keeps it. */
export function historyIdx(): number | null {
  if (typeof window === 'undefined') return null;
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}
